/**
 * ResultsTab Component
 * Patient-friendly display of lab results
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FlaskConical,
  Download,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { PatientLabResult, getOrderResults } from '@/services/patientLabService';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';

interface ResultsTabProps {
  orderId: string;
  sessionToken: string;
}

const ResultsTab: React.FC<ResultsTabProps> = ({ orderId, sessionToken }) => {
  const { toast } = useToast();
  const [results, setResults] = useState<PatientLabResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedResultId, setExpandedResultId] = useState<string | null>(null);

  useEffect(() => {
    loadResults();
  }, [orderId, sessionToken]);

  const loadResults = async () => {
    try {
      setLoading(true);
      const data = await getOrderResults(orderId, sessionToken);
      setResults(data);
      if (data.length > 0) {
        setExpandedResultId(data[0].id);
      }
    } catch (error: any) {
      console.error('Error loading results:', error);
      toast({
        title: 'Error',
        description: 'Failed to load lab results',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getInterpretationColor = (
    interpretation?: 'normal' | 'high' | 'low' | 'critical'
  ) => {
    switch (interpretation) {
      case 'normal':
        return 'text-green-400';
      case 'high':
        return 'text-red-400';
      case 'low':
        return 'text-yellow-400';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-slate-400';
    }
  };

  const getInterpretationIcon = (
    interpretation?: 'normal' | 'high' | 'low' | 'critical'
  ) => {
    switch (interpretation) {
      case 'normal':
        return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      case 'high':
        return <ArrowUpRight className="h-4 w-4 text-red-400" />;
      case 'low':
        return <ArrowDownRight className="h-4 w-4 text-yellow-400" />;
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Minus className="h-4 w-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <Card className="border-slate-700 bg-slate-800">
        <CardContent className="p-8 text-center">
          <FlaskConical className="h-12 w-12 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-400">No results available yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {results.map(result => {
        const isExpanded = expandedResultId === result.id;

        return (
          <Card
            key={result.id}
            className="border-slate-700 bg-slate-800"
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FlaskConical className="h-5 w-5 text-blue-400" />
                    {result.testName}
                  </CardTitle>
                  <div className="flex items-center gap-2 mt-2">
                    {result.interpretation && (
                      <Badge
                        variant={
                          result.interpretation === 'normal'
                            ? 'default'
                            : 'destructive'
                        }
                        className="text-xs"
                      >
                        {result.interpretation.toUpperCase()}
                      </Badge>
                    )}
                    {result.collectionDate && (
                      <span className="text-xs text-slate-400">
                        Collected:{' '}
                        {format(parseISO(result.collectionDate), 'MMM d, yyyy')}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setExpandedResultId(isExpanded ? null : result.id)
                  }
                >
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className="space-y-4">
                {/* Markers */}
                {result.markers && result.markers.length > 0 && (
                  <div className="space-y-3">
                    {result.markers.map((marker, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-white">
                                {marker.name}
                              </h4>
                              {marker.interpretation &&
                                getInterpretationIcon(marker.interpretation)}
                            </div>
                            {marker.loincCode && (
                              <p className="text-xs text-slate-400 mt-1">
                                LOINC: {marker.loincCode}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold text-white">
                              {marker.value} {marker.unit}
                            </div>
                            {marker.interpretation && (
                              <Badge
                                variant={
                                  marker.interpretation === 'normal'
                                    ? 'default'
                                    : 'destructive'
                                }
                                className="text-xs mt-1"
                              >
                                {marker.interpretation}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {marker.referenceRange && (
                          <div className="mt-2 pt-2 border-t border-slate-600">
                            <p className="text-xs text-slate-400">
                              Reference Range:{' '}
                              {marker.referenceRange.text ||
                                (marker.referenceRange.min !== undefined &&
                                marker.referenceRange.max !== undefined
                                  ? `${marker.referenceRange.min}-${marker.referenceRange.max} ${marker.unit}`
                                  : 'N/A')}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Technical Info */}
                {(result.loincCode || result.loincDisplay) && (
                  <div className="pt-4 border-t border-slate-700">
                    <details className="text-sm">
                      <summary className="cursor-pointer text-slate-400 hover:text-slate-300">
                        Technical Information
                      </summary>
                      <div className="mt-2 space-y-1 text-xs text-slate-400">
                        {result.loincCode && (
                          <p>LOINC Code: {result.loincCode}</p>
                        )}
                        {result.loincDisplay && (
                          <p>LOINC Display: {result.loincDisplay}</p>
                        )}
                        {result.resultDate && (
                          <p>
                            Result Date:{' '}
                            {format(parseISO(result.resultDate), 'MMM d, yyyy')}
                          </p>
                        )}
                      </div>
                    </details>
                  </div>
                )}

                {/* Note */}
                <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-300">
                    Your provider will review these results and contact you if any
                    follow-up is needed.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default ResultsTab;
